import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { useAuthStore, type AuthUser, type OrganisationSummary, type OrgSummary } from '@/stores/auth';
import { Button, Input } from '@/features/common/ui';
import { AuthShell } from './LoginPage';

export function RegisterPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setSession = useAuthStore((s) => s.setSession);
  const [form, setForm] = useState({ name: '', email: '', password: '', orgName: '' });

  const register = useMutation({
    mutationFn: () => api.post<{ user: AuthUser; orgs: OrgSummary[]; organisations?: OrganisationSummary[] }>('/auth/register', form),
    onSuccess: (data) => {
      setSession(data.user, data.orgs, data.organisations);
      queryClient.clear();
      navigate('/');
    },
  });

  const error = register.error instanceof ApiError ? register.error : undefined;
  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <AuthShell title="Create your account" subtitle="Free during early access.">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          register.mutate();
        }}
      >
        <Input label="Your name" required value={form.name} onChange={set('name')} error={error?.fieldError('name')} />
        <Input
          label="Business name"
          required
          value={form.orgName}
          onChange={set('orgName')}
          error={error?.fieldError('orgName')}
          hint="This becomes your first book, and is the name on your invoices. You can change it later."
        />
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          required
          value={form.email}
          onChange={set('email')}
          error={error?.fieldError('email')}
        />
        <Input
          label="Password"
          type="password"
          autoComplete="new-password"
          required
          value={form.password}
          onChange={set('password')}
          error={error?.fieldError('password')}
          hint="At least 10 characters. Length matters more than symbols."
        />

        {error && !error.errors?.length ? (
          <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error.message}
          </p>
        ) : null}

        <p className="text-xs text-muted-foreground">
          By creating an account you agree to the{' '}
          <Link to="/terms" className="text-primary hover:underline">
            terms
          </Link>{' '}
          and the{' '}
          <Link to="/privacy" className="text-primary hover:underline">
            privacy policy
          </Link>
          .
        </p>

        <Button type="submit" className="w-full" loading={register.isPending}>
          Create account
        </Button>
      </form>

      <p className="mt-4 text-center text-sm text-muted-foreground">
        Already have an account?{' '}
        <Link to="/login" className="font-medium text-primary hover:underline">
          Sign in
        </Link>
      </p>
    </AuthShell>
  );
}
